/**
 * SDKLauncher Component
 * Handles Prizeout SDK initialization and display
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { POButton, POButtonType } from '@prizeout/prizeout-ui';
import { useForm } from 'react-hook-form';

import { Alert } from '../../components/alert/alert';
import { FormInput } from '../../components/form-input/form-input';
import { useAppSelector, useSDKLauncher } from '../../hooks';
import { selectHasValidEmail } from '../../store/slices';
import { validationRules } from '../../utils/validation';

import './sdk-launcher.scss';

interface SDKEmailFormInputs {
    email: string;
    confirmEmail: string;
}

export interface SDKLauncherProps {
    autoLaunch?: boolean;
}

export const SDKLauncher: React.FC<SDKLauncherProps> = ({ autoLaunch = false }) => {
    const { launchSDK, isWidgetLoaded, error: sdkError, isLoading: sdkLoading } = useSDKLauncher();
    const hasValidEmail = useAppSelector(selectHasValidEmail);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
        watch,
    } = useForm<SDKEmailFormInputs>({ mode: 'onChange' });

    const watchedEmail = watch('email');

    const [error, setError] = useState<string | null>(null);
    const hasAttemptedAutoLaunch = useRef(false);
    const [isAutoLaunching, setIsAutoLaunching] = useState(autoLaunch && hasValidEmail);

    useEffect(() => {
        if (autoLaunch && hasValidEmail && !isWidgetLoaded && !hasAttemptedAutoLaunch.current && !sdkLoading) {
            hasAttemptedAutoLaunch.current = true;
            setIsAutoLaunching(true);
            launchSDK();
        }
    }, [autoLaunch, hasValidEmail, isWidgetLoaded, launchSDK, sdkLoading]);

    useEffect(() => {
        if (sdkError && isAutoLaunching) {
            setIsAutoLaunching(false);
        }
    }, [sdkError, isAutoLaunching]);

    const onSubmit = useCallback(() => {
        setError(null);
        try {
            setIsAutoLaunching(true);
            launchSDK();
        } catch {
            setError('Failed to save email. Please try again.');
            setIsAutoLaunching(false);
        }
    }, [launchSDK]);

    const handleLaunchClick = useCallback(() => {
        setIsAutoLaunching(true);
        launchSDK();
    }, [launchSDK]);

    if (isAutoLaunching && !sdkError && !isWidgetLoaded) {
        return null;
    }

    if (hasValidEmail && sdkError) {
        return (
            <div className="sdk-launcher sdk-launcher--error">
                <div className="sdk-launcher__error">
                    <Alert type="error" message={sdkError} />
                </div>
                <div className="sdk-launcher__actions">
                    <POButton label="Try Again" onClick={handleLaunchClick} isLoading={sdkLoading} />
                </div>
            </div>
        );
    }

    if (hasValidEmail) {
        return null;
    }

    return (
        <div className="sdk-launcher">
            <p className="sdk-launcher__prompt">What email address would you like your gift card sent to?</p>

            {error && (
                <div className="sdk-launcher__error">
                    <Alert type="error" message={error} />
                </div>
            )}

            <form
                id="sdk-email-form"
                className="sdk-launcher__form"
                onSubmit={handleSubmit(onSubmit)}
                autoComplete="off"
            >
                <FormInput
                    id="sdk-email"
                    label="Enter Your Email Address"
                    type="email"
                    placeholder="Enter Your Email Address"
                    error={!!errors.email}
                    errorMessage={errors.email?.message}
                    {...register('email', validationRules.email)}
                />

                <FormInput
                    id="sdk-confirmEmail"
                    label="Confirm Your Email Address"
                    type="email"
                    placeholder="Confirm Your Email Address"
                    error={!!errors.confirmEmail}
                    errorMessage={errors.confirmEmail?.message}
                    {...register('confirmEmail', {
                        required: 'Please confirm your email',
                        validate: (value) => value === watchedEmail || 'Email addresses do not match',
                    })}
                />
            </form>

            <div className="sdk-launcher__actions">
                <POButton
                    label="Confirm Email"
                    isLoading={isSubmitting}
                    isDisabled={!isValid}
                    formId="sdk-email-form"
                    type={POButtonType.SUBMIT}
                />
            </div>
        </div>
    );
};

export default SDKLauncher;
